import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { renderMarkdown } from "../lib/markdown";
import { toast } from "sonner";
import { ArrowLeft, Loader2, ShieldCheck, FileText, Paperclip } from "lucide-react";

export default function AdminReviewDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get(`/reviews/${id}`)
      .then((r) => {
        setData(r.data);
        setNotes(r.data.review.specialist_notes || "");
      })
      .catch(() => toast.error("Failed to load case"));
  }, [id]);

  const approve = async () => {
    if (!notes.trim()) {
      toast.error("Add your clinical notes before approving");
      return;
    }
    if (!window.confirm("Approve & co-sign this consultation? The subscriber will be notified.")) return;
    setSaving(true);
    try {
      await api.post(`/reviews/${id}/approve`, { specialist_notes: notes });
      toast.success("Approved & co-signed");
      nav("/admin/reviews");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Approval failed");
    } finally {
      setSaving(false);
    }
  };

  if (!data)
    return (
      <Layout>
        <div className="text-[var(--pa-text-muted)] flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading case…
        </div>
      </Layout>
    );

  const { review, consultation, files, subscriber } = data;
  const isReviewed = review.status === "reviewed";

  return (
    <Layout>
      <Link
        to="/admin/reviews"
        className="inline-flex items-center gap-1 text-sm text-[var(--pa-text-muted)] hover:text-[var(--pa-gold)] transition"
      >
        <ArrowLeft size={14} /> Review queue
      </Link>

      <div className="mt-4 flex items-start gap-3">
        <ShieldCheck size={24} className="text-[var(--pa-gold)] mt-1" />
        <div>
          <div className="kicker">Specialist Review by Dr. Ronik Seecharan DMD, Prosthodontist — 30 years of clinical practice dedicated to treatment planning excellence</div>
          <h1 className="font-serif text-3xl md:text-4xl text-[var(--pa-text)] mt-1" data-testid="admin-case-title">
            {consultation.module_name}
          </h1>
          <div className="text-xs text-[var(--pa-text-dim)] mt-1">
            Patient {review.patient_initials || "—"} ·{" "}
            Submitted {new Date(review.created_at).toLocaleString()}
            {subscriber && ` · From ${subscriber.full_name}`}
          </div>
        </div>
      </div>

      <div className="gold-divider my-8" />

      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 space-y-6">
          {/* Inputs */}
          <div className="bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-lg p-6">
            <div className="kicker mb-3">Clinical Inputs</div>
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {Object.entries(consultation.inputs).map(([k, v]) => (
                <div key={k}>
                  <dt className="text-xs text-[var(--pa-text-muted)] uppercase tracking-wider">
                    {k.replace(/_/g, " ")}
                  </dt>
                  <dd className="text-[var(--pa-text)]">{String(v) || "—"}</dd>
                </div>
              ))}
            </dl>
          </div>

          {files?.length > 0 && (
            <div className="bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-lg p-6">
              <div className="kicker mb-3 flex items-center gap-2">
                <Paperclip size={12} /> Attached Imaging ({files.length})
              </div>
              <ul className="space-y-2">
                {files.map((f) => (
                  <li key={f.id} className="flex items-center justify-between text-sm">
                    <span className="text-[var(--pa-text)]">{f.original_filename}</span>
                    <span className="text-xs text-[var(--pa-text-dim)]">{f.category}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* AI Consultation */}
          <div className="bg-[var(--pa-surface)] border border-[var(--pa-border-active)] rounded-lg p-6">
            <div className="flex items-center gap-2 mb-3">
              <FileText size={14} className="text-[var(--pa-gold)]" />
              <span className="kicker">AI Consultation</span>
            </div>
            <div
              className="prose-clinical"
              data-testid="admin-case-ai-response"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(consultation.response) }}
            />
          </div>
        </div>

        {/* Review panel */}
        <div className="lg:col-span-5">
          <div className="bg-[var(--pa-surface)] border border-[var(--pa-gold)] rounded-lg p-6 sticky top-4">
            <div className="kicker mb-3">Specialist Notes</div>
            <textarea
              rows={10}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="admin-notes"
              disabled={isReviewed}
              placeholder="Clinical observations, modifications, or override recommendations…"
              className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md text-[var(--pa-text)] text-sm focus:border-[var(--pa-gold)] focus:outline-none p-3 disabled:opacity-70"
            />

            {!isReviewed ? (
              <button
                onClick={approve}
                disabled={saving}
                data-testid="admin-approve"
                className="mt-4 w-full bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold rounded-md px-6 py-3 hover:bg-[var(--pa-gold-hover)] transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Approving…
                  </>
                ) : (
                  <>
                    <ShieldCheck size={14} /> Approve & Co-Sign
                  </>
                )}
              </button>
            ) : (
              <div className="mt-4 p-3 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-xs text-emerald-400">
                ✓ Approved by {review.approved_by_name} on{" "}
                {review.approved_at ? new Date(review.approved_at).toLocaleString() : "—"}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
